import { ShiftDate } from '../../types';
import { DateCell, Side } from './types';

type Dates = {
  startDate: Date | null;
  endDate: Date | null;
};

type InputFocuses = {
  isStartDateFocus: boolean;
  isEndDateFocus: boolean;
};

export type DatePickerState = {
  left: DateCell;
  right: DateCell;
  inputFocuses?: InputFocuses;
};

export type ChangeShiftParams = Partial<ShiftDate> & {
  sideDate?: Side;
};

const initialStateCell: DateCell = {
  date: null,
  shift: {
    left: 0,
    right: 0,
  },
};

export const initialState: DatePickerState = {
  left: initialStateCell,
  right: initialStateCell,
  inputFocuses: {
    isStartDateFocus: false,
    isEndDateFocus: false,
  },
};

const ActionType = {
  DATES: 'changeDates',
  CELLS: 'changeCells',
  CURRENT_CELLS: 'changeCurrentCells',
  SHIFT: 'changeShift',
  SET_START_FOCUS: 'cigarette/setStartFocus',
  SET_END_FOCUS: 'cigarette/setEndFocus',
  UPDATE: 'updateDatePickerState',
} as const;

type ActionType = typeof ActionType[keyof typeof ActionType];

export type Action =
  | { type: typeof ActionType.DATES; payload: Dates }
  | {
      type: typeof ActionType.CURRENT_CELLS;
      payload: Pick<DatePickerState, 'left' | 'right'>;
    }
  | {
      type: typeof ActionType.SHIFT;
      payload: Partial<ShiftDate> & { sideDate: Side };
    }
  | {
      type: typeof ActionType.SET_START_FOCUS;
    }
  | {
      type: typeof ActionType.SET_END_FOCUS;
    }
  | {
      type: typeof ActionType.UPDATE;
      payload: DatePickerState;
    };

export const reducer = (state: DatePickerState, action: Action): DatePickerState => {
  switch (action.type) {
    case ActionType.DATES: {
      const { startDate, endDate } = action.payload;
      const left = {
        ...state.left,
        date: startDate,
      };
      const right = {
        ...state.right,
        date: endDate,
      };
      return { ...state, left, right };
    }
    case ActionType.SHIFT: {
      const { sideDate, ...shift } = action.payload;

      const cell = {
        ...state[sideDate],
        shift: { ...state[sideDate].shift, ...shift },
      };

      return {
        ...state,
        [sideDate]: cell,
      };
    }
    case ActionType.SET_START_FOCUS: {
      return {
        ...state,
        inputFocuses: {
          isStartDateFocus: true,
          isEndDateFocus: false,
        },
      };
    }
    case ActionType.SET_END_FOCUS: {
      return {
        ...state,
        inputFocuses: {
          isStartDateFocus: false,
          isEndDateFocus: true,
        },
      };
    }
    case ActionType.UPDATE: {
      return { ...action.payload };
    }
    default:
      throw new Error('Incorrect action');
  }
};

export const changeShiftAction = ({ sideDate = 'left', ...shift }: ChangeShiftParams) => ({
  type: ActionType.SHIFT,
  payload: { sideDate, ...shift },
});

export const changeDates = (payload: Dates) => ({
  type: ActionType.DATES,
  payload,
});

export const changeCurrentCells = (payload: Pick<DatePickerState, 'left' | 'right'>) => ({
  type: ActionType.CURRENT_CELLS,
  payload,
});

export const setStartDateFocus = () => ({
  type: ActionType.SET_START_FOCUS,
});

export const setEndDateFocus = () => ({
  type: ActionType.SET_END_FOCUS,
});

export const updateDatepickerState = (payload: DatePickerState) => ({
  type: ActionType.UPDATE,
  payload,
});
